import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaBuilding, FaList, FaTag, FaTags } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import '../../assets/styles/categoryDropdown.css';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const CategoryDropdown = ({
    categories = [],
    brands = [],
    companies = [],
    loading = false,
    loadingBrands = false,
    loadingCompanies = false,
    compact = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [activeTab, setActiveTab] = useState('categories');
    const dropdownRef = useRef();
    const timeoutRef = useRef(null);

    const closeMenu = useCallback(() => {
        if (isClosing) return;
        setIsClosing(true);
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
            timeoutRef.current = null;
        }, 300);
    }, [isClosing]);

    const toggleMenu = () => {
        if (isOpen) {
            closeMenu();
        } else {
            if (isClosing) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
                setIsClosing(false);
            }
            setIsOpen(true);
        }
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const hasCategories = categories.length > 0;
    const hasBrands = brands.length > 0;
    const hasCompanies = companies.length > 0;

    const availableTabs = [
        hasCategories ? 'categories' : null,
        hasBrands ? 'brands' : null,
        hasCompanies ? 'companies' : null,
    ].filter(Boolean);

    useEffect(() => {
        if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
            setActiveTab(availableTabs[0]);
        }
    }, [activeTab, availableTabs]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && isOpen && !isClosing) {
                closeMenu();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, isClosing, closeMenu]);

    const showMenu = isOpen || isClosing;

    const isMobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    useBodyScrollLock(showMenu && isMobileViewport);

    if ((loading && loadingBrands && loadingCompanies) || (!hasCategories && !hasBrands && !hasCompanies)) return null;

    const categoryItems = [
        { _id: '', name: 'Todas las Categorias', isAll: true },
        { _id: 'discounts', name: 'Descuentos', isDiscounts: true },
        ...categories,
    ];

    const brandItems = [{ _id: '', name: 'Todas las Marcas', isAllBrands: true }, ...brands];
    const companyItems = [{ _id: '', name: 'Todas las Tiendas', isAllCompanies: true }, ...companies];

    const visibleItems = activeTab === 'brands'
        ? brandItems
        : activeTab === 'companies'
            ? companyItems
            : categoryItems;

    return (
        <div className="category-dropdown" ref={dropdownRef}>
            <button
                className={`category-toggle-btn ${isOpen ? 'active' : ''} ${compact ? 'compact' : ''}`}
                type="button"
                onClick={toggleMenu}
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <FaList className="category-icon" />
                <span className={`ms-2 d-none ${compact ? 'd-none' : 'd-sm-inline'}`}>Categorías</span>
            </button>

            {showMenu && (
                <div className={`category-menu-container ${isClosing ? 'closing' : ''}`}>
                    <div className="category-menu-header">
                        <h5 className="mb-0">Explorar</h5>
                        <button
                            className="btn-close d-md-none"
                            onClick={closeMenu}
                            aria-label="Cerrar"
                        ></button>
                    </div>

                    {availableTabs.length > 1 && (
                        <div className="category-menu-tabs" role="tablist" aria-label="Explorar por tipo">
                            {hasCategories && (
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'categories'}
                                    className={`category-menu-tab ${activeTab === 'categories' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('categories')}
                                >
                                    <FaList />
                                    <span>Categorias</span>
                                </button>
                            )}
                            {hasBrands && (
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'brands'}
                                    className={`category-menu-tab ${activeTab === 'brands' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('brands')}
                                >
                                    <FaTag />
                                    <span>Marcas</span>
                                </button>
                            )}
                            {hasCompanies && (
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'companies'}
                                    className={`category-menu-tab ${activeTab === 'companies' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('companies')}
                                >
                                    <FaBuilding />
                                    <span>Tiendas</span>
                                </button>
                            )}
                        </div>
                    )}

                    <div className="category-section-label">
                        {activeTab === 'brands'
                            ? 'Filtrar por marca'
                            : activeTab === 'companies'
                                ? 'Explorar tiendas'
                                : 'Filtrar por categoria'}
                    </div>

                    <div className="category-grid">
                        {visibleItems.map((item) => (
                            <Link
                                key={`${activeTab}-${item._id || item.ruc || item.name || item.tradeName || 'all'}`}
                                to={
                                    item.isDiscounts
                                        ? '/discounts'
                                        : activeTab === 'brands'
                                            ? item._id
                                                ? `/brand/${item._id}`
                                                : '/'
                                            : activeTab === 'companies'
                                                ? item.ruc
                                                    ? `/company/${item.ruc}`
                                                    : '/'
                                            : item._id
                                                ? `/category/${item._id}`
                                            : '/'
                                }
                                className="category-item-link"
                                onClick={closeMenu}
                            >
                                <div className="category-item-content">
                                    <div
                                        className={`category-image-wrapper ${item.isAll ? 'all-categories' : ''} ${item.isDiscounts ? 'discount-categories' : ''} ${item.isAllBrands ? 'all-brands' : ''} ${item.isAllCompanies ? 'all-companies' : ''}`}
                                    >
                                        {(item.logo || item.image || item.logoUrl) && !item.isAll && !item.isDiscounts && !item.isAllBrands && !item.isAllCompanies ? (
                                            <img
                                                src={item.logo || item.image || item.logoUrl}
                                                alt={item.name || item.tradeName || 'Elemento'}
                                                className="category-img"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '/images/default.jpg';
                                                }}
                                            />
                                        ) : (
                                            <div className="category-placeholder">
                                                {item.isDiscounts
                                                    ? <FaTags />
                                                    : activeTab === 'brands'
                                                        ? <FaTag />
                                                        : activeTab === 'companies'
                                                            ? <FaBuilding />
                                                            : <FaList />}
                                            </div>
                                        )}
                                    </div>
                                    <span className="category-item-name">{item.name || item.tradeName}</span>
                                </div>
                            </Link>
                        ))}
                    </div>

                    <div className="category-menu-footer d-none d-md-block">
                        <small className="text-muted">{'Encuentra todo lo que buscas para tu hogar'}</small>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryDropdown;
